// Address autocomplete is powered by Mapbox Search Box (proxied through our
// backend so the access token never lives in the browser bundle).
// Nominatim is kept as a fallback if our proxy is unreachable.
// OSRM continues to handle driving-distance routing (free).

const NOMINATIM = 'https://nominatim.openstreetmap.org/search';
const OSRM = 'https://router.project-osrm.org/route/v1/driving';
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const MAPBOX_SUGGEST = `${BACKEND}/api/geo/suggest`;
const MAPBOX_RETRIEVE = `${BACKEND}/api/geo/retrieve`;
const MAPBOX_FORWARD = `${BACKEND}/api/geo/forward`;
const MAPBOX_ENABLED = !!BACKEND;

// Service area policy — 2-hour radius at ~60 mph
export const MAX_DISTANCE_MILES = 3000;

// Default operating anchor — used as a fallback when no geolocation/anchor is
// provided so autocomplete still biases to the local service area instead of
// returning unrelated results from across the country.
// West Reading, PA 19611 (admin-overridable via /api/public/geo-anchor)
export const DEFAULT_ANCHOR = { lat: 40.3337, lng: -75.9474 };

// Tightness of the "local bias" viewbox passed to Nominatim. The service area
// (MAX_DISTANCE_MILES) is still enforced separately for actual booking
// validation — this just controls how aggressively local results are surfaced.
export const LOCAL_BIAS_MILES = 60;

// Lazy-loaded admin-configured anchor. We fetch once per session, cache in
// memory, and fall back to the hardcoded DEFAULT_ANCHOR if the request fails.
let _publicAnchorPromise = null;
let _publicAnchor = null;
async function fetchPublicAnchor() {
  if (_publicAnchor) return _publicAnchor;
  if (_publicAnchorPromise) return _publicAnchorPromise;
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return { lat: DEFAULT_ANCHOR.lat, lng: DEFAULT_ANCHOR.lng, biasMiles: LOCAL_BIAS_MILES };
  _publicAnchorPromise = fetch(`${base}/api/public/geo-anchor`, { headers: { Accept: 'application/json' } })
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      if (data && typeof data.lat === 'number' && typeof data.lng === 'number') {
        _publicAnchor = {
          lat: data.lat,
          lng: data.lng,
          biasMiles: Number(data.biasMiles) || LOCAL_BIAS_MILES,
        };
      } else {
        _publicAnchor = { lat: DEFAULT_ANCHOR.lat, lng: DEFAULT_ANCHOR.lng, biasMiles: LOCAL_BIAS_MILES };
      }
      return _publicAnchor;
    })
    .catch(() => {
      _publicAnchor = { lat: DEFAULT_ANCHOR.lat, lng: DEFAULT_ANCHOR.lng, biasMiles: LOCAL_BIAS_MILES };
      return _publicAnchor;
    });
  return _publicAnchorPromise;
}

// Approx degrees per mile (at mid-US latitude)
const MI_PER_DEG_LAT = 69;
const MI_PER_DEG_LNG = 53;

// localStorage keys (v2 — bumped 2026-06-01 when sort order was reverted to
// Nominatim's native relevance ranking)
const CACHE_KEY = 'link_geo_cache_v2';
const RECENTS_KEY = 'link_geo_recents_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const CACHE_MAX_ENTRIES = 200;
const RECENTS_MAX = 8;

function safeRead(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function safeWrite(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota / disabled */ }
}

function cacheGet(query, anchorKey) {
  const c = safeRead(CACHE_KEY, {});
  const k = `${anchorKey}|${query.toLowerCase().trim()}`;
  const hit = c[k];
  if (hit && (Date.now() - hit.t) < CACHE_TTL_MS) return hit.v;
  return null;
}
function cacheSet(query, anchorKey, val) {
  const c = safeRead(CACHE_KEY, {});
  const k = `${anchorKey}|${query.toLowerCase().trim()}`;
  c[k] = { t: Date.now(), v: val };
  // Trim oldest entries if over cap
  const keys = Object.keys(c);
  if (keys.length > CACHE_MAX_ENTRIES) {
    keys.sort((a, b) => c[a].t - c[b].t);
    keys.slice(0, keys.length - CACHE_MAX_ENTRIES).forEach(rk => delete c[rk]);
  }
  safeWrite(CACHE_KEY, c);
}

export function getRecentAddresses() {
  return safeRead(RECENTS_KEY, []);
}
export function addRecentAddress(item) {
  if (!item || !item.lat || !item.lng) return;
  const compact = {
    id: `recent_${item.lat}_${item.lng}`,
    short: item.short,
    display: item.display || item.short,
    lat: item.lat, lng: item.lng,
    city: item.city || '', state: item.state || '',
    isRecent: true,
    t: Date.now(),
  };
  const list = safeRead(RECENTS_KEY, []).filter(r => r.id !== compact.id);
  list.unshift(compact);
  safeWrite(RECENTS_KEY, list.slice(0, RECENTS_MAX));
}

function viewboxAround({ lat, lng }, miles) {
  const dLat = miles / MI_PER_DEG_LAT;
  const dLng = miles / MI_PER_DEG_LNG;
  // Nominatim viewbox = "left,top,right,bottom" i.e. minLng, maxLat, maxLng, minLat
  return `${lng - dLng},${lat + dLat},${lng + dLng},${lat - dLat}`;
}

function anchorKey(anchor) {
  if (!anchor) return 'noanchor';
  return `${anchor.lat.toFixed(2)},${anchor.lng.toFixed(2)}`;
}

function dedupeByCoords(arr) {
  const seen = new Set();
  return arr.filter(x => {
    const k = `${x.lat.toFixed(5)}|${x.lng.toFixed(5)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

async function fetchNominatim(url, signal) {
  try {
    const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}

function mapItem(item) {
  return {
    id: item.place_id,
    display: item.display_name,
    short: formatShort(item),
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
    city: item.address?.city || item.address?.town || item.address?.village || item.address?.county || '',
    state: item.address?.state || '',
    importance: item.importance || 0,
    raw: item,
  };
}

// ---- Mapbox Search Box session token ----
// One session token per user search session = 1 billable session for many
// suggest calls + one retrieve. Persisted in sessionStorage so refreshes
// in the same tab don't churn billing.
function getMapboxSessionToken() {
  try {
    const KEY = 'link_mapbox_session_v1';
    let t = sessionStorage.getItem(KEY);
    if (!t) {
      t = (crypto?.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(KEY, t);
    }
    return t;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// Mapbox suggestion → app suggestion shape. NOTE: coords come from /retrieve,
// not /suggest, so lat/lng start null and are populated when the user picks.
function mapMapboxSuggestion(s) {
  const display = s.full_address || s.place_formatted || s.address || s.name;
  // Build a clean "short" label — name + street if both present, else display
  const shortLabel = s.name && s.address ? `${s.name} — ${s.address}` : (s.name || s.address || display);
  return {
    id: s.mapbox_id,
    mapbox_id: s.mapbox_id,
    short: shortLabel,
    display,
    place_formatted: s.place_formatted || '',
    feature_type: s.feature_type,
    name: s.name,
    lat: null,
    lng: null,
    city: '',
    state: '',
    _needsRetrieve: true,
  };
}

async function searchAddressMapbox(query, { limit, signal, anchor, biasMiles }) {
  const session = getMapboxSessionToken();
  const proximity = `${anchor.lng},${anchor.lat}`;
  // NOTE: we intentionally do NOT pass `bbox` here. `bbox` is a hard filter —
  // it silently drops every result outside the box, which meant far-away
  // destinations (e.g. booking a ride from PA → Florida) returned zero hits.
  // `proximity` alone gives us the same "prefer local" ranking without
  // excluding cross-country matches. `biasMiles` is kept for signature
  // compatibility with callers.
  void biasMiles; // intentionally unused
  const url =
    `${MAPBOX_SUGGEST}?q=${encodeURIComponent(query)}` +
    `&session=${encodeURIComponent(session)}` +
    `&proximity=${proximity}` +
    `&limit=${limit}`;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const data = await res.json();
    return (data.suggestions || []).map(mapMapboxSuggestion);
  } catch {
    return null;
  }
}

// Public — resolve a Mapbox suggestion into coords via the backend proxy.
export async function retrieveMapboxSuggestion(suggestion) {
  if (!suggestion?.mapbox_id || !MAPBOX_ENABLED) return suggestion;
  const session = getMapboxSessionToken();
  const url = `${MAPBOX_RETRIEVE}/${encodeURIComponent(suggestion.mapbox_id)}?session=${encodeURIComponent(session)}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const f = (data.features || [])[0];
    if (!f) return null;
    // Building-entrance accuracy — Mapbox Search Box returns `routable_points`
    // for many POIs (apartment complexes, hospitals, airports, office towers).
    // The first entry is the primary door / drop-off point, which is exactly
    // where the driver should navigate. Falls back to the parcel centroid
    // (feature.geometry.coordinates) when no routable_points are provided.
    const routable = f.properties?.routable_points?.[0];
    const centroid = f.geometry?.coordinates || [];
    const lng = typeof routable?.longitude === 'number' ? routable.longitude : centroid[0];
    const lat = typeof routable?.latitude === 'number' ? routable.latitude : centroid[1];
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    const ctx = f.properties?.context || {};
    return {
      ...suggestion,
      lat,
      lng,
      // Keep the parcel centroid too so the tracking map can still frame the
      // whole building even when we're navigating to a specific entrance.
      centroid: (centroid.length === 2) ? { lat: centroid[1], lng: centroid[0] } : null,
      entrance_used: !!routable,
      city: ctx.place?.name || ctx.locality?.name || ctx.region?.name || '',
      state: ctx.region?.region_code?.replace(/^US-/, '') || ctx.region?.name || '',
      display: f.properties?.full_address || f.properties?.place_formatted || suggestion.display,
      _needsRetrieve: false,
    };
  } catch {
    return null;
  }
}

export async function searchAddress(query, { limit = 8, signal, anchor = null } = {}) {
  const q = (query || '').trim();
  if (q.length < 3) return [];

  // Fall back to the platform's admin-configured anchor (or hardcoded default)
  // so even users without GPS permission get local-first suggestions.
  let effectiveAnchor = anchor;
  let biasMiles = LOCAL_BIAS_MILES;
  if (!effectiveAnchor) {
    const pub = await fetchPublicAnchor();
    effectiveAnchor = { lat: pub.lat, lng: pub.lng };
    biasMiles = pub.biasMiles || LOCAL_BIAS_MILES;
  }

  const ak = anchorKey(effectiveAnchor);
  const cached = cacheGet(q, ak);
  if (cached) return cached;

  // ----- Preferred path: Mapbox Search Box (via backend proxy) -----
  if (MAPBOX_ENABLED) {
    const m = await searchAddressMapbox(q, { limit, signal, anchor: effectiveAnchor, biasMiles });
    if (m && m.length > 0) {
      cacheSet(q, ak, m);
      return m;
    }
    // If Mapbox returned [] we still fall through to Nominatim so very obscure
    // / handwritten road names still hit something.
  }

  // ----- Fallback path: Nominatim (legacy free OSM) -----
  const baseParams = `format=json&addressdetails=1&limit=${limit}&countrycodes=us&dedupe=1`;
  const vbTight = viewboxAround(effectiveAnchor, biasMiles);
  const vbWide = viewboxAround(effectiveAnchor, MAX_DISTANCE_MILES);
  const [p1, p2] = await Promise.all([
    fetchNominatim(`${NOMINATIM}?${baseParams}&q=${encodeURIComponent(q)}&viewbox=${vbTight}&bounded=1`, signal),
    fetchNominatim(`${NOMINATIM}?${baseParams}&q=${encodeURIComponent(q)}&viewbox=${vbWide}`, signal),
  ]);
  const merged = dedupeByCoords([...p1, ...p2].map(mapItem));
  merged.sort((a, b) => {
    const da = haversineMiles(effectiveAnchor, { lat: a.lat, lng: a.lng }) || 9999;
    const db = haversineMiles(effectiveAnchor, { lat: b.lat, lng: b.lng }) || 9999;
    return da - db;
  });
  const top = merged.slice(0, limit);
  cacheSet(q, ak, top);
  return top;
}

function formatShort(item) {
  const a = item.address || {};
  const street = [a.house_number, a.road].filter(Boolean).join(' ');
  const city = a.city || a.town || a.village || a.county || '';
  const state = a.state || '';
  return [street, city, state].filter(Boolean).join(', ') || item.display_name;
}

// Haversine in miles — quick client-side sanity check without a network call.
export function haversineMiles(a, b) {
  if (!a || !b) return null;
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Returns driving distance in miles between two {lat, lng} points.
export async function drivingDistanceMiles(from, to) {
  if (!from || !to) return null;

  // Straight-line distance as a safety floor. We use ×1.25 to approximate road
  // routing distance — close enough for fare quoting if OSRM is unreachable.
  const fallback = (() => {
    const m = haversineMiles(from, to);
    return m != null ? m * 1.25 : null;
  })();

  // Hard 8-second timeout — OSRM occasionally rate-limits or stalls and we
  // refuse to let the "Calculating…" spinner deadlock the booking flow.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);

  const url = `${OSRM}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=false&alternatives=false`;
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return fallback;
    const data = await res.json();
    const meters = data?.routes?.[0]?.distance;
    if (meters == null) return fallback;
    return meters / 1609.344;
  } catch {
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}

// One-shot geolocation prompt. Returns {lat, lng} or null if denied / unavailable.
export function getCurrentPosition(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: timeoutMs }
    );
  });
}


// Geocode a single freeform address string. Returns {lat, lng, display} or null.
// Used by the Admin Settings panel so the operator can paste a city/zip and
// automatically resolve it into the anchor lat/lng. Mapbox-first, Nominatim
// fallback.
export async function geocodeAddress(query) {
  const q = (query || '').trim();
  if (q.length < 3) return null;

  if (MAPBOX_ENABLED) {
    const url = `${MAPBOX_FORWARD}?q=${encodeURIComponent(q)}&limit=1`;
    try {
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const f = (data.features || [])[0];
        if (f && f.geometry?.coordinates) {
          const [lng, lat] = f.geometry.coordinates;
          return { lat, lng, display: f.properties?.full_address || f.properties?.place_formatted || q };
        }
      }
    } catch { /* fall through to Nominatim */ }
  }

  // Nominatim fallback
  const url = `${NOMINATIM}?format=json&q=${encodeURIComponent(q)}&limit=1&countrycodes=us`;
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      display: data[0].display_name,
    };
  } catch {
    return null;
  }
}
